import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Calendar,
  Users,
  Building,
  Plane,
  Home,
  UtensilsCrossed,
  CreditCard,
  Car,
  FileText,
  MoreHorizontal,
  TrendingUp,
  TrendingDown,
  PieChart
} from 'lucide-react';
import { formatDate } from '../../utils/date';

const ExpenseDetails = ({ campaign, onClose, onAddExpense, onEditExpense, onDeleteExpense }) => {
  const { t } = useTranslation();

  const expenseTypes = [
    {
      key: 'accommodationMadinah',
      label: t('expenses.accommodationMadinah'),
      icon: Home,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900'
    },
    {
      key: 'accommodationMakkah',
      label: t('expenses.accommodationMakkah'),
      icon: Home,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900'
    },
    {
      key: 'mealsMadinah',
      label: t('expenses.mealsMadinah'),
      icon: UtensilsCrossed,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100 dark:bg-orange-900'
    },
    {
      key: 'mealsMakkah',
      label: t('expenses.mealsMakkah'),
      icon: UtensilsCrossed,
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900'
    },
    {
      key: 'visaFees',
      label: t('expenses.visaFees'),
      icon: CreditCard,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900'
    },
    {
      key: 'airTickets',
      label: t('expenses.airTickets'),
      icon: Plane,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900'
    },
    {
      key: 'internalTransportation',
      label: t('expenses.internalTransportation'),
      icon: Car,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900'
    },
    {
      key: 'programFees',
      label: t('expenses.programFees'),
      icon: FileText,
      color: 'text-teal-600',
      bgColor: 'bg-teal-100 dark:bg-teal-900'
    },
    {
      key: 'miscellaneous',
      label: t('expenses.miscellaneous'),
      icon: MoreHorizontal,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100 dark:bg-gray-900'
    }
  ];

  const getTypeIcon = (type) => {
    return type === 'sold' ? Building : Plane;
  };

  const getTypeColor = (type) => {
    return type === 'sold' 
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  const calculateTotalExpenses = () => {
    return Object.values(campaign.expenses || {}).reduce((total, amount) => total + (amount || 0), 0);
  };

  const getExpensePercentage = (amount) => {
    const total = calculateTotalExpenses();
    return total > 0 ? ((amount / total) * 100).toFixed(1) : 0;
  };

  const totalExpenses = calculateTotalExpenses();
  const profitLoss = campaign.totalRevenue - campaign.totalCosts;
  const isProfitable = profitLoss >= 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('expenses.campaignExpenses')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {campaign.campaignName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Campaign Overview */}
        <div className="p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(campaign.campaignType)}`}>
                {React.createElement(getTypeIcon(campaign.campaignType), { className: "h-3 w-3 mr-1" })}
                {t(`campaigns.${campaign.campaignType}`)}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="h-4 w-4 mr-2" />
              <span>{formatDate(campaign.departureDate)} - {formatDate(campaign.returnDate)}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Users className="h-4 w-4 mr-2" />
              <span>{campaign.maxCapacity} {t('campaigns.travelers')}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <PieChart className="h-4 w-4 mr-2" />
              <span>{Object.keys(campaign.expenses || {}).filter(key => campaign.expenses[key] > 0).length} {t('expenses.categories')}</span>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {t('expenses.totalExpenses')}
                  </p>
                  <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                    {totalExpenses.toLocaleString()} {t('common:currency')}
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    {t('campaigns.totalRevenue')}
                  </p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {campaign.totalRevenue.toLocaleString()} {t('common:currency')}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <div className={`${isProfitable ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-red-50 dark:bg-red-900/20'} p-4 rounded-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isProfitable ? 'text-blue-800 dark:text-blue-200' : 'text-red-800 dark:text-red-200'}`}>
                    {t('campaigns.netProfit')}
                  </p>
                  <p className={`text-2xl font-bold ${isProfitable ? 'text-blue-900 dark:text-blue-100' : 'text-red-900 dark:text-red-100'}`}>
                    {Math.abs(profitLoss).toLocaleString()} {t('common:currency')}
                  </p>
                </div>
                {isProfitable ? (
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-600" />
                )}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('expenses.expensePerTraveler')}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {campaign.maxCapacity > 0 ? (totalExpenses / campaign.maxCapacity).toLocaleString() : '0'} {t('common:currency')}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Expense Categories */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('expenses.expenseBreakdown')}
            </h3>
            <button
              onClick={onAddExpense}
              className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('expenses.addExpense')}
            </button>
          </div>

          {totalExpenses === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('expenses.noExpensesFound')}
              </p>
              <button
                onClick={onAddExpense}
                className="flex items-center mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('expenses.addFirstExpense')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {expenseTypes.map((type) => {
                const amount = campaign.expenses?.[type.key] || 0;
                const percentage = getExpensePercentage(amount);
                
                return (
                  <div
                    key={type.key}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      amount > 0 
                        ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800' 
                        : 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${type.bgColor}`}>
                          {React.createElement(type.icon, {
                            className: `h-5 w-5 ${type.color}`
                          })}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                            {type.label}
                          </h4>
                          {amount > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {percentage}% {t('expenses.ofTotal')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {amount > 0 ? (
                          <>
                            <span className="font-bold text-gray-900 dark:text-white">
                              {amount.toLocaleString()} {t('common:currency')}
                            </span>
                            <button
                              onClick={() => onEditExpense(type.key)}
                              className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                              title={t('common.edit')}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => onDeleteExpense(type.key)}
                              className="p-1 text-red-600 hover:text-red-800 transition-colors"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {t('expenses.notSet')}
                            </span>
                            <button
                              onClick={() => onEditExpense(type.key)}
                              className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                              title={t('expenses.addAmount')}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {amount > 0 && (
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${type.bgColor.replace('dark:bg-', 'dark:bg-').replace('100', '500').replace('900', '400')}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sold Campaign Additional Info */}
        {campaign.campaignType === 'sold' && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-600 bg-purple-50 dark:bg-purple-900/20">
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4">
              {t('campaigns.soldCampaignDetails')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  {t('campaigns.costPrice')}
                </p>
                <p className="text-xl font-bold text-purple-900 dark:text-purple-100">
                  {campaign.costPrice?.toLocaleString()} {t('common:currency')}
                </p>
              </div>
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  {t('campaigns.sellingPrice')}
                </p>
                <p className="text-xl font-bold text-purple-900 dark:text-purple-100">
                  {campaign.sellingPrice?.toLocaleString()} {t('common:currency')}
                </p>
              </div>
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  {t('campaigns.buyer')}
                </p>
                <p className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                  {campaign.buyerCompany}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseDetails;